import {useState, useEffect, useMemo} from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'

import LoginPage from './components/LoginPage'
import CoursesPage from './components/CoursesPage'
import ChatPage from './components/ChatPage'
import SupportPage from './components/SupportPage'
import ConversationModal from './components/ConversationModal';
import AdminChatViewer from './components/AdminChatViewer';
import FeedbackPromotionPage from './components/FeedbackPromotionPage';
import {googleLogout} from '@react-oauth/google'
import axios from 'axios'
import {v4 as uuidv4} from 'uuid'
import axiosInstance from './components/axiosInstance'
import {latestAnnouncement, Modal} from './components/Modal'; // Import the Modal component and latest announcement
import { jwtDecode } from "jwt-decode"
import InstructorPage from './components/InstructorPage'
import LoadingScreen from './components/ui-common/LoadingScreen'
import SystemAnnouncementPage from './components/SystemAnnouncementPage'
import { KeyRound, BookOpen } from 'lucide-react'

const style = {
    appContainer: `container-fluid h-100`,
    appContent: `row justify-content-center h-100`,
    sectionContainer: `col-md-8 col-xl-6 chat`,
}


function setUserProfile(profile) {
    const profileData = {
        data: profile,
        timestamp: new Date().getTime(), // Store the current timestamp in milliseconds
    }
    localStorage.setItem('userProfile', JSON.stringify(profileData))
}

export function getUserProfile() {
    const profileData = localStorage.getItem('userProfile')
    if (profileData) {
        const parsedData = JSON.parse(profileData)
        const currentTime = new Date().getTime()
        const elapsedTime = (currentTime - parsedData.timestamp) / (1000 * 60 * 60) // Convert ms to hours

        // Check if 24 hours have passed
        if (elapsedTime >= 24) {
            localStorage.removeItem('userProfile') // Remove the profile if 24 hours have passed
            return null
        }
        return parsedData.data // Return the profile data if within 24 hours
    }
    return null
}

function MainApp({ restrictedMode = false }) {
    const userProfile = getUserProfile()
    const [credential, setCredential] = useState(null)
    const [profile, setProfile] = useState(userProfile)
    const [user, setUser] = useState(null)

    const [chatHistory, setChatHistory] = useState([])
    const [chatTitles, setChatTitles] = useState({})
    const [currentConversationId, setCurrentConversationId] = useState(null)
    const [currentMessages, setCurrentMessages] = useState([])

    const [courses, setCourses] = useState([])
    const [isCoursesLoading, setIsCoursesLoading] = useState(true); // New loading state
    const [selectedCourse, setSelectedCourse] = useState(null)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isConversationModalOpen, setConversationModalOpen] = useState(false);
    const navigate = useNavigate()
    const location = useLocation()
    // On initial mount, check for Google OAuth code and exchange it.
    useEffect(() => {
      const params = new URLSearchParams(window.location.search)
      const code   = params.get("code")
      if (code) {
        axiosInstance.post("/api/signin", { code })
.           then(({ data }) => {
                // 1) stash the profile locally
                const profile = {
                    name: data.name,
                    email: data.email,
                    picture: data.picture,
                    is_admin: data.is_admin  // Store admin status
                };
                setProfile(profile);
                setUserProfile(profile);
                // 2) clean up the URL and reload so everything else mounts as "signed in"  
                window.history.replaceState({}, "", "/");
                // Let the useEffect handle setting the view to courses after profile is set
          })
          .catch(err => console.error("Signin exchange failed:", err))
      }
    }, [])

    const updatedChatHistory = useMemo(() => {
        return chatHistory.map(conversation => ({
            ...conversation,
            title: chatTitles[conversation.conversation_id] || conversation.title || null
        }));
    }, [chatHistory, chatTitles]);

    const fetchCourses = async () => {
    try {
        setIsCoursesLoading(true);
        const response = await axiosInstance.get('/api/courses', { timeout: 180000 }) // 3 minutes
        setCourses(response.data)
    } catch (error) {
        console.error('Failed to fetch courses:', error)
    } finally {
        setIsCoursesLoading(false);
    }
}

    const informBackendAboutSignIn = async (tokenResponse) => {
        try {
            await axiosInstance.post('/api/signin', {
                token: tokenResponse.credential,
            })
        } catch (error) {
            console.error('Error informing backend about sign-in:', error);
            return null;
        }
    }

    useEffect(() => {
        if (user && user.credential) {
            const claims = jwtDecode(user.credential)
            const profile = {
                name: claims.name,
                email: claims.email,
                picture: claims.picture
            }
            setProfile(profile)
            setUserProfile(profile)
            informBackendAboutSignIn(user)
            fetchCourses()
            window.location.href = '/'
            navigate('/', { replace: true })
        }
    }, [user])

    const logOut = async () => {
        try {
            await axiosInstance.post("/api/logout");
          } catch (err) {
            console.error("Error clearing server session:", err);
          }
        googleLogout()
        setProfile(null)
        setUser(null)
        setChatHistory([])
        setSelectedCourse(null)
        localStorage.removeItem('userProfile')
        navigate('/login', { replace: true })
    }


const fetchChatHistoryOnly = async () => {
    try {
        const response = await axiosInstance.get('/api/conversations');
        return response.data.reverse(); // Reverse to show most recent first
    } catch (error) {
        console.error('Failed to fetch chat history:', error);
        return [];
    }
};

const setChatHistoryFromFetch = async (defaultCourse) => {
    const conversations = await fetchChatHistoryOnly(); // Fetch the conversations
    setChatHistory(conversations); // Update chat history state

    if (conversations.length > 0) {
        setCurrentConversationId(conversations[0].conversation_id);
    } else {
        createNewConversation(defaultCourse); // Create a new conversation if none exist
    }
};


    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const courseParam = urlParams.get('course')?.toLowerCase(); // Get 'course' from URL and convert to lowercase

    const initializeChatHistory = async () => {
        if (profile && courses.length > 0) {
            const availableCourseLabels = courses.map(course => course.label.toLowerCase());
            const availableCourseNames = courses.map(course => course.name.toLowerCase());

            // Fetch conversations without updating state yet
            const conversations = await fetchChatHistoryOnly();
            setChatHistory(conversations); // Set chat history to show all conversations

            // Helper function to find the course by name or label (case-insensitive)
            const findCourseByParam = (param) =>
                courses.find(course => course.name.toLowerCase() === param) ||
                courses.find(course => course.label.toLowerCase() === param);

            // If a course param is present, create a new conversation with that course
            if (courseParam && (availableCourseLabels.includes(courseParam) || availableCourseNames.includes(courseParam))) {
                const matchedCourse = findCourseByParam(courseParam);
                if (matchedCourse) {
                    createNewConversation(matchedCourse.name);
                }
            } else if (conversations.length === 0) {
                // If no course param and no existing conversations, create a new one
                createNewConversation(courses[0].name);
            } else {
                // If there are existing conversations, set the latest one as current
                setCurrentConversationId(conversations[0].conversation_id);
            }
        }
    };

    initializeChatHistory();
    }, [courses]);


    useEffect(() => {
        const currentConversation = chatHistory.find(
            (convo) => convo.conversation_id === currentConversationId,
        )
        setCurrentMessages(currentConversation ? currentConversation.messages : [])
    }, [currentConversationId, chatHistory])

    const setMessages = (newMessages) => {
        setChatHistory(
            chatHistory.map((conversation) => {
                if (conversation.conversation_id === currentConversationId) {
                    return {...conversation, messages: newMessages}
                }
                return conversation
            }),
        )
    }

    const updateMessageById = (id, newValues) => {
        setChatHistory((previousChatHistory) => {
            const updatedHistory = previousChatHistory.map((conversation) => {
                if (conversation.conversation_id === currentConversationId) {
                    const updatedMessages = conversation.messages.map((message) => {
                        if (message.query_uuid === id) {
                            return {...message, ...newValues}
                        }
                        return message
                    })

                    // Check if any message was updated (Optional)
                    const hasUpdated = updatedMessages.some((msg) => msg.query_uuid === id)
                    if (!hasUpdated) {
                        console.error(
                            'No message with the given ID was found in the current conversation.',
                        )
                    }

                    return {...conversation, messages: updatedMessages}
                }
                return conversation
            })
            return updatedHistory
        })
    }

    // Conversations Functions

    const switchConversation = (conversationId) => {
        setCurrentConversationId(conversationId)
    }

    const deleteConversation = async (conversationId) => {
        const isCurrent = conversationId === currentConversationId
        const updatedChatHistory = chatHistory.filter(
            (conversation) => conversation.conversation_id !== conversationId,
        )
        try {
            // Send delete request to the server
            await axiosInstance.delete(`/api/conversations/${conversationId}`)

            setChatHistory(updatedChatHistory)
            if (isCurrent) {
                if (updatedChatHistory.length > 0) {
                    // Use updatedChatHistory for selecting new current conversation
                    setCurrentConversationId(updatedChatHistory[0].conversation_id)
                } else {
                    const newConversation = createNewConversationObj(courses[0].name)
                    setChatHistory([newConversation])
                    setCurrentConversationId(newConversation.conversation_id)
                }
            }
        } catch (error) {
            console.error('Error deleting conversation:', error)
        }
    }

    const createNewConversationObj = (conversationCourse) => {
        const newConversationId = uuidv4()
        const conversationCreationTime = Date.now() / 1000 // TODO: fix
        const courseObj = courses.find(c => c.name === conversationCourse);
        return {
            conversation_id: newConversationId,
            conversation_course: conversationCourse,
            conversation_course_label: courseObj?.label || conversationCourse,
            creation_date: conversationCreationTime,
            messages: [],
        }
    }

    const createNewConversation = (selectedCourse) => {
        const newConversation = createNewConversationObj(selectedCourse)
        setChatHistory((prevChatHistory) => [newConversation, ...prevChatHistory]);
        setCurrentConversationId(newConversation.conversation_id)
    }

    const handleNewConversationClick = () => {
        if (courses.length === 0) {
            console.error('No courses available to create a new conversation.')
            return;
        }
        
        // If we're in chat view and have a selected course, create conversation for that course
        if (location.pathname.startsWith('/chat') && selectedCourse) {
            createNewConversation(selectedCourse.name);
            return;
        }
        
        // Otherwise use the original logic for other views
        if (courses.length === 1) {
            createNewConversation(courses[0].name)
        } else {
            setConversationModalOpen(true);
        }
    };

    const handleSelectCourse = (selectedCourse) => {
        createNewConversation(selectedCourse);
    };

    const handleCourseSelect = (course) => {
        setSelectedCourse(course);
        // Log the enter-course event
        axiosInstance.post('/api/log/enter-course', {
            course_name: course.name,
            course_id: course.id,
        }).catch(() => {});  // fire-and-forget
        // Create new conversation for selected course
        createNewConversation(course.name);
        navigate('/chat');
    };

    // get seen announcement from local storage
    const getSeenAnnouncementId = () => {
        return localStorage.getItem('seenAnnouncementId');
    }

    //  mark the latest announcement as seen
    const markAsSeen = (query_uuid) => {
        localStorage.setItem('seenAnnouncementId', query_uuid);
    }

    // display the latest announcement if unseen
    const displayAnnouncement = () => {
        const seenAnnouncementId = getSeenAnnouncementId();
        if (seenAnnouncementId !== String(latestAnnouncement.id)) {
            setIsModalOpen(true);
            markAsSeen(latestAnnouncement.id);
        }
    }

    const closeModal = () => {
        setIsModalOpen(false);
    }

    // show the announcement modal only after the user has signed in
    useEffect(() => {
        const handlePostLogin = async () => {
            if (profile) {
                // In restricted mode, non-admin users just see the announcement page
                // — skip course fetching and enrollment checks entirely.
                if (restrictedMode && !profile.is_admin) {
                    return;
                }

                displayAnnouncement();
                // Navigate to courses if we're on login page
                if (location.pathname === '/login' || location.pathname === '/') {
                    navigate('/courses', { replace: true });
                }
                try {
                    await fetchCourses();
                } catch (error) {
                    console.error('Failed to fetch courses after login:', error);
                }
            }
        };
        
        handlePostLogin();
    }, [profile]);

    // Redirect to /courses if user refreshes while on /chat (selectedCourse will be null)
    useEffect(() => {
        if (profile && location.pathname.startsWith('/chat') && !selectedCourse) {
            navigate('/courses', { replace: true });
        }
    }, [profile, location.pathname, selectedCourse, navigate]);

    // If we're in the middle of an OAuth callback, show a verifying screen.
    const params = new URLSearchParams(window.location.search);
    if (params.has('code')) {
        return <LoadingScreen icon={<KeyRound />} text="Verifying your login, one moment..." />;
    }

    // Restricted mode: let users log in, but only admins can proceed.
    // Non-admin users see the system-unavailable announcement after logging in.
    if (restrictedMode && profile && !profile.is_admin) {
        return <SystemAnnouncementPage onLogout={logOut} />;
    }

    return (
        <>
            <Routes>
                {/* Login route */}
                <Route
                    path="/login"
                    element={
                        profile ? (
                            <Navigate to="/courses" replace />
                        ) : (
                            <LoginPage
                                clientId="131206229781-trhspf02ot3sh1m0kucamp482gpo5iid.apps.googleusercontent.com"
                                onGoogleLogin={() => {}} // This will be handled by the component itself
                            />
                        )
                    }
                />

                {/* Courses route */}
                <Route
                    path="/courses"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : isCoursesLoading ? (
                            <LoadingScreen icon={<BookOpen />} text="Loading your courses..." />
                        ) : (
                            <CoursesPage
                                user={profile}
                                onCourseSelect={handleCourseSelect}
                                onLogout={logOut}
                                onNavigateToSupport={() => navigate('/support')}
                                onNavigateToInstructor={() => navigate('/instructor')}
                            />
                        )
                    }
                />

                {/* Chat route */}
                <Route
                    path="/chat"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <ChatPage
                                user={profile}
                                selectedCourse={selectedCourse}
                                chatHistory={updatedChatHistory}
                                updateChatTitles={setChatTitles}
                                currentMessages={currentMessages}
                                currentConversationId={currentConversationId}
                                onBackToCourses={() => navigate(-1)}
                                onLogout={logOut}
                                onNewConversation={handleNewConversationClick}
                                onSwitchConversation={switchConversation}
                                onDeleteConversation={deleteConversation}
                                setMessages={setMessages}
                                updateMessageById={updateMessageById}
                                onNavigateToSupport={() => navigate('/support')}
                            />
                        )
                    }
                />

                {/* Support route */}
                <Route
                    path="/support"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <SupportPage
                                user={profile}
                                onBackToChat={() => navigate(-1)}
                                onLogout={logOut}
                            />
                        )
                    }
                />

                {/* Instructor route */}
                <Route
                    path="/instructor"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <InstructorPage
                                user={profile}
                                onBack={() => navigate(-1)}
                                onLogout={logOut}
                                onNavigateToSupport={() => navigate('/support')}
                            />
                        )
                    }
                />

                {/* Admin Viewer route - only accessible to admins */}
                <Route
                    path="/admin/viewer"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <AdminChatViewer
                                user={profile}
                                onBack={() => navigate('/courses')}
                                onLogout={logOut}
                                onNavigateToSupport={() => navigate('/support')}
                            />
                        )
                    }
                />

                {/* Feedback Promotion route - only accessible to admins on staging */}
                <Route
                    path="/admin/feedback-promotion"
                    element={
                        !profile ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <FeedbackPromotionPage />
                        )
                    }
                />

                {/* Default route - redirect to courses if logged in, otherwise to login */}
                <Route
                    path="/"
                    element={
                        <Navigate to={profile ? "/courses" : "/login"} replace />
                    }
                />

                {/* Catch-all route */}
                <Route
                    path="*"
                    element={
                        <Navigate to={profile ? "/courses" : "/login"} replace />
                    }
                />
            </Routes>

            {/* Modals that should appear on any route */}
            <Modal isOpen={isModalOpen} onClose={closeModal} title={latestAnnouncement.title}>
                {latestAnnouncement.content}
            </Modal>
            <ConversationModal
                isOpen={isConversationModalOpen}
                onClose={() => setConversationModalOpen(false)}
                onSelect={handleSelectCourse}
                courses={courses}
            />
        </>
    );
}

export default MainApp
